import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InflowProductPicker } from "@/components/InflowProductPicker";
import { UploadCloud, Loader2, FileText, Trash2, ShieldCheck, ArrowLeft, Sparkles, Plus, CheckCircle2, Circle, Wand2 } from "lucide-react";

const STAGE_KEYS = ["rough_in", "tiling", "tapware", "flooring"];
const STAGE_LABELS = { rough_in: "Rough In", tiling: "Tiles", tapware: "Plumbing Fixtures", flooring: "Flooring" };

export default function PoImport() {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);      // {id, file, stage}
  const [busy, setBusy] = useState(false);
  const [groups, setGroups] = useState(null);   // grouped jobs after parse
  const [committing, setCommitting] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [dest, setDest] = useState({ mode: "new", builder_id: "", job_id: "" });
  const [builders, setBuilders] = useState([]);
  const [jobsForBuilder, setJobsForBuilder] = useState([]);
  const [lockedStages, setLockedStages] = useState([]);

  const loadBuilders = async () => {
    try { const { data } = await client.get("/builders"); setBuilders(data || []); } catch { /* noop */ }
  };
  const loadJobs = async (bid) => {
    setJobsForBuilder([]);
    if (!bid) return;
    try { const { data } = await client.get(`/builders/${bid}`); setJobsForBuilder(data.jobs || []); } catch { /* noop */ }
  };
  const pickJob = async (v) => {
    setDest((d) => ({ ...d, job_id: v }));
    setLockedStages([]);
    if (!v) return;
    try {
      const { data } = await client.get(`/builder-jobs/${v}`);
      const p = data.inflow_pushes || {};
      setLockedStages(Object.keys(p).filter((k) => p[k]));
    } catch { /* noop */ }
  };

  const addFiles = (fl) => {
    const arr = Array.from(fl || []).map((f, i) => ({ id: `${Date.now()}_${i}_${f.name}`, file: f, stage: "auto" }));
    setFiles((p) => [...p, ...arr]);
    if (fileRef.current) fileRef.current.value = "";
  };
  const setFileStage = (id, stage) => setFiles((p) => p.map((f) => (f.id === id ? { ...f, stage } : f)));
  const removeFile = (id) => setFiles((p) => p.filter((f) => f.id !== id));

  const readAll = async () => {
    const poFiles = files.filter((f) => f.stage !== "attachment");
    const attFiles = files.filter((f) => f.stage === "attachment");
    if (!poFiles.length) { toast.error("Add at least one PO file"); return; }
    setBusy(true);
    const parsed = [];
    try {
      for (const f of poFiles) {
        const fd = new FormData();
        fd.append("file", f.file);
        if (f.stage !== "auto") fd.append("stage", f.stage);
        const { data } = await client.post("/po-import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
        parsed.push(data);
      }
      // A PO import is always ONE job — merge every file into a single group so minor
      // address variations (e.g. one file missing the postcode) never split it in two.
      const chosen = { key: "job", builder_id: "", builder_name: "", site_address: "", po_number: "", notes: "", lines: [] };
      for (const p of parsed) {
        chosen.lines.push(...(p.lines || []).map((l) => ({ ...l, confirmed: !l.inflow_product_id })));
        if (!chosen.builder_id && p.suggested_builder?.id) { chosen.builder_id = p.suggested_builder.id; chosen.builder_name = p.suggested_builder.name; }
        if (!chosen.builder_name && (p.suggested_builder?.name || p.customer)) chosen.builder_name = p.suggested_builder?.name || p.customer;
        // Keep the most complete site address (longest wins → the one that includes the postcode).
        const a = p.site_address || "";
        if (a.trim().length > chosen.site_address.trim().length) chosen.site_address = a;
        if (!chosen.po_number && p.po_number) chosen.po_number = p.po_number;
        if (p.notes) chosen.notes = chosen.notes ? `${chosen.notes}\n${p.notes}` : p.notes;
      }
      chosen.title = chosen.site_address || (chosen.po_number ? `PO ${chosen.po_number}` : (chosen.builder_name || "Imported PO"));
      const arr = [chosen];
      setGroups(arr);
      setAttachments(attFiles.map((f) => f.file));
      toast.success(`Read ${parsed.length} PO(s) → 1 job${attFiles.length ? ` · ${attFiles.length} plan(s) attached` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read a PO");
    } finally {
      setBusy(false);
    }
  };

  const setGroup = (key, patch) => setGroups((gs) => gs.map((g) => (g.key === key ? { ...g, ...patch } : g)));
  const setLine = (key, id, patch) => setGroups((gs) => gs.map((g) => (g.key === key ? { ...g, lines: g.lines.map((l) => (l.id === id ? { ...l, ...patch } : l)) } : g)));
  const removeLine = (key, id) => setGroups((gs) => gs.map((g) => (g.key === key ? { ...g, lines: g.lines.filter((l) => l.id !== id) } : g)));
  const bulkStage = (key, stage) => setGroups((gs) => gs.map((g) => (g.key === key ? { ...g, lines: g.lines.map((l) => ({ ...l, stage })) } : g)));
  const isExact = (l) => l.match_confidence === "exact" || l.match_source === "alias";
  const setConfirm = (key, id, v) => setGroups((gs) => gs.map((g) => (g.key === key ? { ...g, lines: g.lines.map((l) => (l.id === id ? { ...l, confirmed: v } : l)) } : g)));
  const acceptAllExact = (key) => setGroups((gs) => gs.map((g) => (g.key === key ? { ...g, lines: g.lines.map((l) => (isExact(l) ? { ...l, confirmed: true } : l)) } : g)));

  const commitAll = async () => {
    const existing = dest.mode === "existing";
    if (existing && (!dest.builder_id || !dest.job_id)) { toast.error("Pick a builder and a job to import into"); return; }
    if (existing && lockedStages.length) {
      const used = [...new Set(groups.flatMap((g) => g.lines.map((l) => l.stage)))].filter((s) => lockedStages.includes(s));
      if (used.length) { toast.error(`These stages are already invoiced and can't be overwritten: ${used.map((s) => STAGE_LABELS[s]).join(", ")}`); return; }
    }
    for (const g of groups) {
      if (!existing && !g.builder_id && !g.builder_name.trim()) { toast.error("Add a customer / builder for each job"); return; }
      if (g.lines.length === 0) { toast.error("Each job needs at least one line"); return; }
      if (g.lines.some((l) => !l.stage)) { toast.error("Every line needs a stage"); return; }
      const unconf = g.lines.filter((l) => l.inflow_product_id && !l.confirmed);
      if (unconf.length) { toast.error(`Confirm ${unconf.length} mapped line(s) before importing`); return; }
    }
    setCommitting(true);
    try {
      let firstJob = null, n = 0;
      for (const g of groups) {
        const stages = {};
        for (const l of g.lines) { (stages[l.stage] = stages[l.stage] || []).push(l); }
        const payload = existing
          ? { job_id: dest.job_id, po_number: g.po_number, stages }
          : { builder_id: g.builder_id || null, builder_name: g.builder_name, po_number: g.po_number, site_address: g.site_address, title: g.title, notes: g.notes, stages };
        const { data } = await client.post("/po-import/commit", payload);
        firstJob = firstJob || data.job_id; n++;
      }
      if (firstJob && attachments.length) {
        for (const af of attachments) {
          try {
            const fd = new FormData(); fd.append("file", af);
            await client.post(`/builder-jobs/${firstJob}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          } catch { /* non-blocking */ }
        }
      }
      toast.success(existing ? "PO added to the selected job" : `Created ${n} builder job(s)`);
      nav(firstJob ? `/builder-jobs/${firstJob}` : "/builders");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't import PO");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid="po-import-page">
      <h1 className="font-display text-2xl font-bold text-ink">Import PO</h1>
      <p className="mt-1 text-sm text-muted-foreground">Upload one or more customer POs for a single job — set a stage per file (or let AI decide), review, then create a draft Builder job. All files you add here are combined into one job.</p>

      {!groups ? (
        <div className="mt-6 space-y-4" data-testid="po-select">
          <input ref={fileRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls,image/*,application/pdf" className="hidden" data-testid="po-file-input" onChange={(e) => addFiles(e.target.files)} />
          <button onClick={() => fileRef.current?.click()} data-testid="po-upload-btn"
            className="flex w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 py-10 text-center transition-colors hover:border-primary/40 hover:bg-accent/40">
            <UploadCloud className="h-7 w-7 text-primary" />
            <span className="font-display text-base font-bold text-ink">Add PO files (PDF, image, CSV or Excel)</span>
            <span className="text-xs text-muted-foreground">You can add several at once — one per stage, or a full-house PO.</span>
          </button>

          {files.length > 0 && (
            <div className="space-y-2 rounded-2xl border border-slate-100 bg-white p-4">
              {files.map((f) => (
                <div key={f.id} data-testid={`po-file-${f.id}`} className="flex flex-col gap-2 rounded-xl border border-slate-100 p-3 sm:flex-row sm:items-center sm:justify-between">
                  <span className="flex min-w-0 items-center gap-2 text-sm text-ink"><FileText className="h-4 w-4 shrink-0 text-primary" /><span className="truncate">{f.file.name}</span></span>
                  <div className="flex items-center gap-2">
                    <Select value={f.stage} onValueChange={(v) => setFileStage(f.id, v)}>
                      <SelectTrigger className="h-9 w-48" data-testid={`po-file-stage-${f.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto (AI decides per line)</SelectItem>
                        {STAGE_KEYS.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}
                        <SelectItem value="attachment">Plan / attachment (not a PO)</SelectItem>
                      </SelectContent>
                    </Select>
                    <button onClick={() => removeFile(f.id)} data-testid={`po-file-remove-${f.id}`} className="grid h-9 w-9 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-error"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-1">
                <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1 text-xs font-medium text-primary"><Plus className="h-3.5 w-3.5" />Add more</button>
                <Button onClick={readAll} disabled={busy} data-testid="po-read-btn" className="gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}{busy ? "Reading…" : `Read ${files.length} PO${files.length > 1 ? "s" : ""}`}</Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-6 space-y-6" data-testid="po-review">
          <button onClick={() => { setGroups(null); setDest({ mode: "new", builder_id: "", job_id: "" }); setLockedStages([]); }} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-ink"><ArrowLeft className="h-4 w-4" />Start over</button>

          <div className="rounded-2xl border border-slate-100 bg-white p-4" data-testid="po-destination">
            <Label className="mb-2 block text-xs">Where should this PO go?</Label>
            <Select value={dest.mode} onValueChange={(v) => { setDest({ mode: v, builder_id: "", job_id: "" }); setLockedStages([]); if (v === "existing") loadBuilders(); }}>
              <SelectTrigger className="h-9 sm:w-72" data-testid="po-dest-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">Create a new job (default)</SelectItem>
                <SelectItem value="existing">Add to an existing job</SelectItem>
              </SelectContent>
            </Select>
            {dest.mode === "existing" && (
              <>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div><Label className="mb-1 block text-xs">Builder</Label>
                    <Select value={dest.builder_id} onValueChange={(v) => { setDest((d) => ({ ...d, builder_id: v, job_id: "" })); setLockedStages([]); loadJobs(v); }}>
                      <SelectTrigger className="h-9" data-testid="po-dest-builder"><SelectValue placeholder="Pick a builder" /></SelectTrigger>
                      <SelectContent>{builders.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="mb-1 block text-xs">Job</Label>
                    <Select value={dest.job_id} onValueChange={pickJob} disabled={!dest.builder_id}>
                      <SelectTrigger className="h-9" data-testid="po-dest-job"><SelectValue placeholder={dest.builder_id ? "Pick a job" : "Pick a builder first"} /></SelectTrigger>
                      <SelectContent>{jobsForBuilder.length ? jobsForBuilder.map((j) => <SelectItem key={j.id} value={j.id}>{j.title || (j.po_number ? `PO ${j.po_number}` : j.id)}</SelectItem>) : <div className="px-2 py-2 text-xs text-muted-foreground">No jobs for this builder yet</div>}</SelectContent>
                    </Select>
                  </div>
                </div>
                {lockedStages.length > 0 && (
                  <p className="mt-2 rounded-lg bg-warning/15 px-3 py-2 text-[11px] font-medium text-warning" data-testid="po-locked-stages">
                    Already invoiced &amp; locked: {lockedStages.map((s) => STAGE_LABELS[s] || s).join(", ")}. Lines for these stages can't be added — remove them or import into a different job.
                  </p>
                )}
                <p className="mt-2 text-[11px] text-muted-foreground">Imported lines will be added to this job's stages. The customer/title/address fields below are ignored.</p>
              </>
            )}
          </div>

          {groups.map((g) => (
            <div key={g.key} data-testid={`po-job-${g.key}`} className="rounded-2xl border border-slate-100 bg-white p-4">
              <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div><Label className="mb-1 block text-xs">Customer / builder</Label>
                  <Input value={g.builder_name} onChange={(e) => setGroup(g.key, { builder_name: e.target.value, builder_id: "" })} data-testid="po-builder-name" />
                  {g.builder_id && <span className="mt-1 inline-block text-[11px] font-medium text-success">Matched existing builder — will attach</span>}
                </div>
                <div><Label className="mb-1 block text-xs">Job title</Label><Input value={g.title} onChange={(e) => setGroup(g.key, { title: e.target.value })} data-testid="po-title" /></div>
                <div><Label className="mb-1 block text-xs">Site address</Label><Input value={g.site_address} onChange={(e) => setGroup(g.key, { site_address: e.target.value })} data-testid="po-site-address" /></div>
                <div><Label className="mb-1 block text-xs">PO number</Label><Input value={g.po_number} onChange={(e) => setGroup(g.key, { po_number: e.target.value })} data-testid="po-number" /></div>
              </div>

              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="flex flex-wrap items-center gap-2 text-sm font-bold text-ink">Line items <span className="text-xs font-normal text-muted-foreground">· {g.lines.length}</span>
                  {g.lines.some((l) => !l.stage) && <span className="rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-semibold text-warning">{g.lines.filter((l) => !l.stage).length} need a stage</span>}
                  {g.lines.filter((l) => l.match_source === "name" && l.inflow_product_id).length > 0 && <span className="rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-semibold text-warning" data-testid={`po-guessed-count-${g.key}`}>{g.lines.filter((l) => l.match_source === "name" && l.inflow_product_id).length} AI-guessed · confirm</span>}
                  {g.lines.filter((l) => !l.inflow_product_id).length > 0 && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500" data-testid={`po-unmatched-count-${g.key}`}>{g.lines.filter((l) => !l.inflow_product_id).length} not matched</span>}
                  {g.lines.filter((l) => l.inflow_product_id && !l.confirmed).length > 0 && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary" data-testid={`po-toconfirm-count-${g.key}`}>{g.lines.filter((l) => l.inflow_product_id && !l.confirmed).length} to confirm</span>}
                </span>
                <div className="flex items-center gap-2">
                  {g.lines.some((l) => isExact(l) && !l.confirmed) && (
                    <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" data-testid={`po-accept-exact-${g.key}`} onClick={() => acceptAllExact(g.key)}>
                      <CheckCircle2 className="h-3.5 w-3.5 text-success" />Accept all exact ({g.lines.filter((l) => isExact(l) && !l.confirmed).length})
                    </Button>
                  )}
                  <Select onValueChange={(v) => bulkStage(g.key, v)}>
                    <SelectTrigger className="h-8 w-44 text-xs" data-testid={`po-bulk-stage-${g.key}`}><SelectValue placeholder="Apply stage to all" /></SelectTrigger>
                    <SelectContent>{STAGE_KEYS.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3">
                {g.lines.map((l) => (
                  <div key={l.id} data-testid={`po-line-${l.id}`} className={`rounded-xl border p-3 ${l.inflow_product_id && !l.confirmed ? "border-warning/40 bg-warning/5" : l.inflow_product_id && l.confirmed ? "border-success/30 bg-success/5" : "border-slate-100"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <Input value={l.description} onChange={(e) => setLine(g.key, l.id, { description: e.target.value })} className="h-9 flex-1 text-sm" data-testid={`po-line-desc-${l.id}`} />
                      <button onClick={() => removeLine(g.key, l.id)} data-testid={`po-line-remove-${l.id}`} className="grid h-9 w-9 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-error"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <div><Label className="mb-1 block text-[11px] text-muted-foreground">Qty</Label><Input type="number" min="0" value={l.qty} onChange={(e) => setLine(g.key, l.id, { qty: e.target.value })} className="h-9 text-right text-sm" data-testid={`po-line-qty-${l.id}`} /></div>
                      <div><Label className="mb-1 block text-[11px] text-muted-foreground">Unit price</Label><Input type="number" min="0" step="0.01" value={l.unit_price ?? ""} onChange={(e) => setLine(g.key, l.id, { unit_price: e.target.value })} className="h-9 text-right text-sm" data-testid={`po-line-price-${l.id}`} /></div>
                      <div className="col-span-2"><Label className="mb-1 block text-[11px] text-muted-foreground">Stage</Label>
                        <Select value={l.stage || ""} onValueChange={(v) => setLine(g.key, l.id, { stage: v })}>
                          <SelectTrigger className={l.stage ? "h-9" : "h-9 border-warning/50"} data-testid={`po-line-stage-${l.id}`}><SelectValue placeholder="Choose stage" /></SelectTrigger>
                          <SelectContent>{STAGE_KEYS.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="mt-2"><Label className="mb-1 block text-[11px] text-muted-foreground">inFlow product</Label><InflowProductPicker item={l} onSelect={(patch) => setLine(g.key, l.id, { ...patch, match_source: "manual", confirmed: true })} />
                      {l.match_source === "alias" && l.inflow_product_id && <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary" data-testid={`po-line-alias-${l.id}`}><Wand2 className="h-3 w-3" />Matched from a learned code</span>}
                      {l.match_source === "sku" && l.inflow_product_id && <span className="mt-1 inline-block rounded-full bg-success/15 px-2 py-0.5 text-[11px] font-semibold text-success" data-testid={`po-line-exact-${l.id}`}>Matched by code</span>}
                      {l.match_source === "name" && l.inflow_product_id && <span className="mt-1 inline-block rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-semibold text-warning" data-testid={`po-line-guess-${l.id}`}>AI matched by name</span>}
                      {!l.inflow_product_id && (l.suggestions?.length > 0) && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={`po-line-suggestions-${l.id}`}>
                          <span className="text-[11px] font-semibold text-muted-foreground">Did you mean:</span>
                          {l.suggestions.map((s) => (
                            <button key={s.inflow_product_id} type="button" data-testid={`po-line-suggest-${l.id}-${s.inflow_product_id}`}
                              onClick={() => setLine(g.key, l.id, { inflow_product_id: s.inflow_product_id, inflow_product_name: s.inflow_product_name, sku: s.sku, match_source: "suggestion", confirmed: true })}
                              className="rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-[11px] font-medium text-primary transition-colors hover:bg-primary/10">
                              {s.sku}{s.inflow_product_name ? ` · ${s.inflow_product_name}` : ""}
                            </button>
                          ))}
                        </div>
                      )}
                      {!l.inflow_product_id && !(l.suggestions?.length > 0) && <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500" data-testid={`po-line-nomatch-${l.id}`}>No catalogue match — search above</span>}
                      {l.inflow_product_id && (
                        <button type="button" data-testid={`po-line-confirm-${l.id}`} onClick={() => setConfirm(g.key, l.id, !l.confirmed)}
                          className={`mt-2 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${l.confirmed ? "bg-success/15 text-success" : "border border-warning/50 bg-warning/10 text-warning hover:bg-warning/20"}`}>
                          {l.confirmed ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                          {l.confirmed ? "Confirmed" : "Tap to confirm this match"}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-4 w-4 text-success" />Nothing is pushed to inFlow — push each stage from the job when ready.</span>
            <Button onClick={commitAll} disabled={committing} data-testid="po-commit-btn" className="gap-2">{committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}{dest.mode === "existing" ? "Add to selected job" : `Create ${groups.length} builder job${groups.length > 1 ? "s" : ""}`}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
